import { OverlayCarousel } from '../carousel/overlay-carousel';
import BevSafeText from '../bev-safe-text';
import { TopFiveRecommendations } from '../types';
import Link from '../link';
import Image from '../image';
import { cn } from '@fshr/shared-react';
import { useTranslations } from '@fshr/context/i18n-context';
import { forwardRef, MouseEvent } from 'react';
import { useTealium } from '@fshr/context/tealium-context';
import { useRecoTracking } from '../for-you/analytics/recoTracker';
import { useRecoImpressionBatchObserver } from '../for-you/hooks/useRecoImpression';
type AllowedColors = 'green' | 'yellow' | 'orange' | 'red' | 'gray';

const isAllowedColor = (color: string | undefined): color is AllowedColors => {
	if (!color) {
		return false;
	}
	return ['red', 'green', 'yellow', 'orange', 'red', 'gray'].includes(color?.toLowerCase());
};

const COLOR_MAP: Record<AllowedColors, { color: string }> = {
	green: {
		color: 'bg-[#2ad062]',
	},
	yellow: {
		color: 'bg-[#df943c]',
	},
	orange: {
		color: 'bg-[#df943c]',
	},
	red: {
		color: 'bg-[#ff1e1e]',
	},
	gray: {
		color: 'bg-neutral-900',
	},
};

const StatusIndicator = ({ text, color }: { text?: string; color?: string }) => {
	if (!text) return null;

	const lowerCaseColor = color?.toLocaleLowerCase();

	const selectedColor = COLOR_MAP[isAllowedColor(lowerCaseColor) ? lowerCaseColor : 'gray'].color;

	return (
		<div
			className="absolute left-0 right-0 bottom-0 w-full flex justify-center items-center z-1 gap-[6px] py-[4px] px-[5px] text-caption min-h-[15%]"
			style={{
				background: 'rgba(3, 3, 3, 0.6)',
				backdropFilter: 'blur(8px)',
			}}
		>
			<span className={cn(['inline-block rounded-full w-[7px] h-[7px]', selectedColor])} />
			<p className="text-text-on-image-primary line-clamp-1">
				<span className="text-[10px] tracking-[6%]!">{text}</span>
			</p>
		</div>
	);
};

export const ImageSection = ({
	image,
	color,
	text,
	altText,
	imageContainerClassName,
}: {
	image?: string;
	text?: string;
	color?: string;
	altText?: string;
	imageContainerClassName?: string;
}) => {
	const t = useTranslations();
	return (
		<div className="w-full flex flex-row gap-[15px] items-start justify-start">
			<div
				className={cn(
					'relative w-[140px] h-[140px] min-w-[140px] min-h-[140px] flex-shrink-0',
					imageContainerClassName
				)}
			>
				<Image
					roughSize="s"
					src={image}
					className="h-full w-full object-cover"
					alt={altText ?? t('Ancilliary recommendation')}
				/>
				{text ? <StatusIndicator text={text} color={color} /> : null}
			</div>
		</div>
	);
};

type CardWrapperProps = {
	url?: string;
	propertyOrsCode?: string;
	sku?: string;
	datalakeRecoId?: string;
	containerClassName?: string;
	onClick?: () => void;
	children: React.ReactNode;
};

export const CardWrapper = forwardRef<HTMLElement, CardWrapperProps>(
	({ children, url, propertyOrsCode, sku, datalakeRecoId, containerClassName, onClick }, ref) => {
		const { addToRecommendationsCookie } = useTealium();

		if (!url) {
			return (
				<div className={cn('flex flex-col spacing-stack-xs w-[140px]', containerClassName)}>
					{children}
				</div>
			);
		}

		const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
			event.preventDefault();

			onClick?.();

			addToRecommendationsCookie({
				ancillary: sku,
				datalakeRecoId: [
					{
						sku: sku || 'na',
						datalakeRecoId: datalakeRecoId ?? 'na',
						ors: propertyOrsCode || 'na',
						type: 'ancillary',
					},
				],
			});

			setTimeout(() => {
				window.location.href = url;
			}, 150);
		};

		return (
			<Link
				ref={ref as React.Ref<HTMLAnchorElement>}
				href={url}
				intent="unstyled"
				className={cn(
					'flex flex-col spacing-stack-xs cursor-pointer w-[140px]',
					containerClassName
				)}
				onClick={handleClick}
			>
				{children}
			</Link>
		);
	}
);

CardWrapper.displayName = 'CardWrapper';

type AncillaryItemProps = {
	item: TopFiveRecommendations;
	index: number;
	placement?: string;
	onClick: (position: number) => void;
	trackRecoImpressions?: boolean;
};

const AncillaryRecommendationItem = forwardRef<HTMLElement, AncillaryItemProps>(
	({ item, index, placement, onClick, trackRecoImpressions = true }, ref) => {
		const position = index + 1;

		const impressionRef = useRecoImpressionBatchObserver(
			{
				reco_placement: placement ?? 'na',
				reco_position: String(position),
				reco_enhancement_sku: item?.sku || 'na',
				reco_enhancement_name: item.title,
				reco_enhancement_property_name: item.propertyOrsCode ?? 'na',
				enhancement_datalake_reco_id: item?.datalakeRecoId
					? item?.datalakeRecoId?.toString()
					: 'na',
			},
			'ancillary',
			trackRecoImpressions
		);

		return (
			<CardWrapper
				ref={impressionRef}
				url={item.url}
				propertyOrsCode={item.propertyOrsCode}
				sku={item.sku}
				datalakeRecoId={item.datalakeRecoId}
				onClick={() => onClick(position)}
			>
				<ImageSection
					altText={item.image.altText}
					image={item.image.url}
					text={item.status?.message}
					color={item.status?.color}
				/>

				<div className="w-full flex flex-col text-subtitle2 text-text-secondary spacing-stack-2xs min-h-[65px] justify-between items-between">
					<h3 className="line-clamp-2">
						<BevSafeText>{item.title}</BevSafeText>
					</h3>
					<div className="flex flex-row flex-wrap spacing-inline-2xs">
						<p>{item.price?.total}</p>
						<p className="text-caption">{item.price?.type ? `/person` : ''}</p>
					</div>
				</div>
			</CardWrapper>
		);
	}
);

AncillaryRecommendationItem.displayName = 'AncillaryRecommendationItem';

export const AncilliariesRecommendationsList = ({
	ancilliariesRecommendations,
	gap = 10,
	placement,
	trackRecoImpressions = true,
}: {
	ancilliariesRecommendations: TopFiveRecommendations[];
	gap?: number;
	placement?: string;
	trackRecoImpressions?: boolean;
}) => {
	const { trackEnhancementRecoClick } = useRecoTracking();

	return (
		<OverlayCarousel
			gap={gap}
			itemClassName="w-[140px]"
			disableButtons={false}
			buttonsType={['arrow']}
			carouselItemsContainer="flex-row items-stretch"
		>
			{ancilliariesRecommendations.map((item, index) => (
				<AncillaryRecommendationItem
					key={item.sku}
					item={item}
					index={index}
					placement={placement}
					trackRecoImpressions={trackRecoImpressions}
					onClick={position =>
						trackEnhancementRecoClick({
							sku: item.sku,
							name: item.title,
							propertyName: item.propertyOrsCode ?? 'na',
							propertyCode: item.propertyOrsCode || 'na',
							enhancementDatalakeRecoId: item.datalakeRecoId ?? 'na',
							placement: placement ?? 'na',
							position: index + 1,
						})
					}
				/>
			))}
		</OverlayCarousel>
	);
};
